import { Roles } from "../../mp-utils/generic-utils/Enums/index"
import { EventAction } from "../../mp-utils/generic-utils/Events/types"
import { FilterSettings } from "../states"
import {
  ContentType,
  CubeRequestParams,
  FilterTypeItem,
  FilterRouteConfig,
  UserRole
} from "../types"
import {
  ActivityEventsLabels,
  ContentTypeLabels,
  LicenseStatus,
  GradeFilterOption
} from "./labels"
import { States } from "../../mp-database/types/GeoState"
import {
  getWorkkeysReplaceTerm,
  getWorkkeysReplaceUrlContentType
} from "../helpers/workkeysReplaceTerms"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"

export const FILTER_ITEMS: FilterTypeItem[] = [
  { key: "date", label: "Date", icon: "calendar-range", type: "date" },
  { key: "test", label: "Test", icon: "book-copy", type: "program" },
  { key: "level", label: "Level", icon: "chart-line", type: "select" },
  { key: "content", label: "Content", icon: "ballot", wide: true },
  { key: "state", label: "State", icon: "flag-usa" },
  { key: "organization", label: "Organization", icon: "sitemap" },
  { key: "district", label: "District", icon: "city" },
  {
    key: "school",
    label: getWorkkeysReplaceTerm("school", true),
    icon: "school"
  },
  { key: "grade", label: "Grade", icon: "graduation-cap", type: "select" },
  {
    key: "teacher",
    label: getWorkkeysReplaceTerm("teacher", true),
    icon: "chalkboard-teacher"
  },
  { key: "class", label: "Class", icon: "users-class" },
  {
    key: "student",
    label: getWorkkeysReplaceTerm("student", true),
    icon: "user"
  },
  {
    key: "userStatus",
    label: "User Status",
    icon: "user-group",
    type: "select"
  },
  { key: "contentType", label: "Content Type", icon: "books", type: "select" },
  {
    key: "usageContentType",
    label: "Content Type",
    icon: "books",
    type: "usage-content-type"
  },
  {
    key: "activity",
    label: "Activity",
    icon: "arrow-pointer",
    type: "select"
  },
  {
    key: "userRole",
    label: "User Role",
    icon: "user-group",
    type: "select"
  },
  {
    key: "adminRole",
    label: "User Role",
    icon: "user-group",
    type: "select"
  },
  {
    key: "score",
    label: "Score",
    icon: "chart-line",
    type: "score-range",
    wide: true
  },
  {
    key: "skill",
    label: "Skill",
    icon: "list-tree",
    type: "skill",
    wide: true
  },
  {
    key: "licenseStatus",
    label: "License Status",
    icon: "id-card",
    type: "select"
  },
  {
    key: "status",
    label: "Status",
    icon: "circle-check",
    type: "select"
  },
  { key: "questionsContent", label: "Content", icon: "ballot" }
]

export const FILTERS_BY_ROLE: Record<string, string[]> = {
  [Roles.Super_Administrator]: [
    "date",
    "test",
    "level",
    "state",
    "organization",
    "district",
    "school"
  ],
  [Roles.Sales_Rep]: ["date", "test", "level", "state", "district", "school"],
  [Roles.ACT_Admin]: [
    "date",
    "test",
    "level",
    "state",
    "organization",
    "district",
    "school"
  ],
  [Roles.District_Admin]: ["date", "test", "level", "school"],
  [Roles.ACT_Multi_District_Admin]: [
    "date",
    "test",
    "level",
    // Commented out, because `state` filter is not filtered by `organization`
    // "state",
    "district",
    "school"
  ],
  [Roles.School_Admin]: ["date", "test", "level", "teacher"],
  [Roles.Teacher]: ["date", "test", "level", "school", "class", "student"],
  [Roles.Student]: []
}

export const DEFAULT_FILTER_BY_ROUTE = {
  get default() {
    return []
  },
  "/reporting/content/content-library": [
    "test",
    "contentType",
    "standard",
    "skill"
  ],
  "/reporting/teaching-tools/bell-ringers": ["teacher", "class"],
  "/reporting/teaching-tools/bell-ringers/:entityId": ["teacher", "class"],
  "/reporting/teaching-tools/books": ["test"],
  "/reporting/teaching-tools/books/mastery-curriculum": [
    "test",
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/books/book/:ebookId": ["test"],
  "/reporting/teaching-tools/study-plan": ["test"],
  "/reporting/teaching-tools/study-plan/:subpage": ["test"],
  "/reporting/teaching-tools/study-plan/:subpage/:entityType": ["test"],
  "/reporting/teaching-tools/test-administration": [
    "test",
    "district",
    "school",
    "teacher",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/test-administration/:entityId": [
    "test",
    "district",
    "school",
    "teacher",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/test-administration/:entityId/edit": ["test"],
  "/reporting/teaching-tools/mastery-curriculum": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/mastery-curriculum/:program/:subPage": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/study-hall": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/study-hall/:program/:subPage": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/assignments": [
    "test",
    "organization",
    "district",
    "school",
    "teacher",
    "class",
    "student",
    "contentType"
  ],
  // Assignment detail uses local content-aware filter chips from assignment assignees.
  "/reporting/teaching-tools/assignments/:entityId": [],
  "/reporting/insights/performance/scores": ["content"]
}

export const ORG_FILTERS: string[] = [
  "state",
  "organization",
  "district",
  "school",
  "grade",
  "teacher",
  "class",
  "student"
]

export const ORG_FILTERS_SOURCES: Record<string, string> = {
  state: "org_view_states",
  organization: "org_view_organizations",
  district: "org_view_districts",
  school: "org_view_schools",
  teacher: "org_view_teachers",
  class: "org_view_classes",
  student: "org_view_students"
}

export const ORG_FILTERS_BY_ROLE: Record<string, string[]> = {
  [Roles.Super_Administrator]: [
    "state",
    "organization",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student"
  ],
  [Roles.ACT_Admin]: [
    "state",
    "organization",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student"
  ],
  [Roles.District_Admin]: ["school", "grade", "teacher", "class", "student"],
  [Roles.ACT_Multi_District_Admin]: [
    "state",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student"
  ],
  [Roles.School_Admin]: ["grade", "teacher", "class", "student"],
  [Roles.Teacher]: ["class", "student", "grade", "school"],
  [Roles.Student]: []
}

export const FIXED_ROLE_FILTERS: Record<string, string[]> = {
  [Roles.Super_Administrator]: [],
  [Roles.ACT_Admin]: ["test"],
  [Roles.District_Admin]: ["state", "district"],
  [Roles.ACT_Multi_District_Admin]: ["organization"],
  [Roles.School_Admin]: ["state", "district", "school"],
  [Roles.Teacher]: ["state", "district", "school", "teacher"],
  [Roles.Student]: ["state", "district", "school", "student"]
}

export type FiltersWithCustomSettings = {
  [role in keyof typeof Roles | "DEFAULT" | "ALL"]?: FilterSettings
}

const defaultFiltersCustomization: FiltersWithCustomSettings = {
  ALL: {
    test: { isSingleSelect: true },
    district: { isSingleSelect: true },
    school: { isSingleSelect: true },
    class: { isSingleSelect: true },
    teacher: { isSingleSelect: true },
    student: { isSingleSelect: true }
  }
}

export const FILTERS_CUSTOMIZATIONS: Record<string, FiltersWithCustomSettings> =
  {
    "/reporting/teaching-tools/study-plan": defaultFiltersCustomization,
    "/reporting/teaching-tools/study-plan/:subpage":
      defaultFiltersCustomization,
    "/reporting/teaching-tools/study-plan/:subpage/:entityType":
      defaultFiltersCustomization,
    "/reporting/teaching-tools/bell-ringers": defaultFiltersCustomization,
    "/reporting/teaching-tools/bell-ringers/:entityId":
      defaultFiltersCustomization,
    "/reporting/teaching-tools/mastery-curriculum": defaultFiltersCustomization,
    "/reporting/teaching-tools/mastery-curriculum/:program/:subPage":
      defaultFiltersCustomization,
    "/reporting/teaching-tools/study-hall": defaultFiltersCustomization,
    "/reporting/teaching-tools/study-hall/:program/:subPage":
      defaultFiltersCustomization,
    "/reporting/insights/performance/content-mastery":
      defaultFiltersCustomization
  }

export const FILTERS_BY_ROUTE: Record<string, string[]> = {
  get default() {
    return this["/reporting/organization"]
  },
  "/reporting/organization": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student"
    // "userStatus"
  ],
  "/reporting/organization/organizations": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"
  ],
  "/reporting/organization/states": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/states/:entityId": [
    "date"
    // "test",
    // "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/districts": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/districts/:entityId": [
    "date"
    // "test",
    // "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/admins": [
    "date",
    "state",
    "organization",
    "district",
    "school",
    // "adminRole",
    "userStatus"
  ],
  "/reporting/organization/admins/:entityId": ["date"],
  "/reporting/organization/institutions": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/institutions/:entityId": [
    "date",
    // "test",
    "teacher",
    "class"
    // "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/instructors": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/instructors/:entityId": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    "class",
    "student"
    // "userStatus",
    // "usageContentType",
    // "content",
    // "activity"

    // "reportingCategory"
    // "standard"
    // "score"
    // "segment"
  ],
  "/reporting/organization/classes": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/classes/:entityId": [
    "date",
    // "test",
    // "grade",
    "student"
    // "userStatus",
    // "content",
    // "usageContentType",
    // "activity"

    // "reportingCategory"
    // "standard"
    // "score"
    // "segment"
  ],
  "/reporting/organization/learners": [
    "date",
    // "test",
    "state",
    "organization",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student",
    "userStatus"

    // "score"
    // "segment"
  ],
  "/reporting/organization/learners/:entityId": [
    "date"
    // "test",
    // "usageContentType",
    // "content",
    // "activity"

    // "reportingCategory"
    // "standard"
    // "score"
  ],

  "/reporting/content/content-library": [
    "date",
    "test",
    "level",
    "state",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "contentType",
    "skill"
    // "segment"
    // "studyPlanStatus"
    // "contentStatus"
    // "reportingCategory"
    // "standard"
  ],
  "/reporting/content/content-library/:entityType/:entityId": [
    "date",
    "test",
    "contentType",
    "skill",
    "standard",
    "state",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "activity"
    // "segment"
    // "reportingCategory"
    // "standard"
  ],

  "/reporting/insights/usage/learners-usage": [
    "date",
    // "test",
    "state",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student",
    "usageContentType"
    // "segment"
    // "reportingCategory"
    // "standard"
  ],
  "/reporting/insights/usage/instructors-usage": [
    "date",
    // "test",
    "state",
    "district",
    "organization",
    "school",
    "teacher",
    "class",
    "student"
    // "usageContentType"
    // "segment"
    // "reportingCategory"
    // "standard"
  ],
  "/reporting/insights/usage/activity-log": [
    "date",
    // "test",
    "state",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "usageContentType",
    // "content",
    "activity",
    "userRole"
    // "segment"
    // "reportingCategory"
    // "standard"
  ],
  "/reporting/insights/performance/scores": [
    "date",
    // "test",
    "level",
    "state",
    "district",
    "school",
    "grade",
    "teacher",
    "class",
    "student",
    "content",
    "contentType"

    // "userStatus"
    // "segment",
  ],
  "/reporting/insights/performance/questions": [
    "date",
    "test",
    "level",
    "state",
    "organization",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "questionsContent",
    "skill"
    // "segment",
    // "reportingCategory"
    // "standard"
  ],
  "/reporting/insights/performance/answers": [
    "date",
    // "test",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    "userStatus",
    "content"
  ],
  "/reporting/insights/performance/goals": [
    "date",
    // "test",
    "state",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student",
    // "segment",
    // "userStatus",
    "score"
    // "content"
  ],
  "/reporting/insights/performance/content-mastery": [
    // "date",
    // "test",
    // "state",
    "district",
    "school",
    // "grade",
    "teacher",
    "class",
    "student"
    // "segment",
    // "userStatus",
    // "score",
    // "content"
  ],
  "/reporting/teaching-tools/bell-ringers": [
    "test",
    "class",
    "state",
    "district",
    "teacher",
    "school"
  ],
  "/reporting/teaching-tools/bell-ringers/:entityId": [
    "test",
    "class",
    "state",
    "district",
    "teacher",
    "school"
  ],
  "/reporting/teaching-tools/books": [
    // "test"
  ],
  "/reporting/teaching-tools/books/mastery-curriculum": [
    // "test",
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/books/book/:eBookId": [
    // "test"
  ],
  "/reporting/teaching-tools/study-plan": ["test"],
  "/reporting/teaching-tools/study-plan/:subpage": ["test"],
  "/reporting/teaching-tools/study-plan/:subpage/:entityType": ["test"],
  "/reporting/teaching-tools/test-administration": [
    // "test",
    "district",
    "school",
    "teacher",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/test-administration/:entityId": [
    // "test",
    "district",
    "school",
    "teacher",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/test-administration/:entityId/edit": [
    // "test",
    "district",
    "school",
    "teacher",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/mastery-curriculum": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/mastery-curriculum/:program/:subPage": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/study-hall": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/study-hall/:program/:subPage": [
    "district",
    "school",
    "class",
    "student"
  ],
  "/reporting/teaching-tools/assignments": [
    "test",
    "organization",
    "district",
    "school",
    "teacher",
    "class",
    "student",
    "contentType"
  ],
  "/reporting/teaching-tools/assignments/:entityId": [],
  "/reporting/organization/license-assign": [
    // Removed "date" filter
    "state",
    "district",
    "school",
    "teacher",
    "class",
    "grade",
    "licenseStatus"
  ],
  "/reporting/organization/license-pools": [
    // Only show state, organization, district, and school filters
    "state",
    "organization",
    "district"
    // Removed: "school" "date", "teacher", "class", "student"
  ],
  "/reporting/profile": [] // No filters for profile page
}

export const FILTER_SEARCH_PARAMS: Record<string, string> = {
  state: "org_view_states.code",
  district: "org_view_districts.districtName",
  school: "org_view_schools.name",
  class: "org_view_classes.name",
  teacher: "org_view_teachers.fullName",
  student: "org_view_students.studentFullname",
  content: "content_view_list.name"
}

export const PROGRAM_FILTER_REQUEST_PARAM = "tests.program"
export const SUBJECT_FILTER_REQUEST_PARAM = "tests.subject"

export const FILTERS_REQUEST_PARAMS: Record<string, CubeRequestParams> = {
  state: {
    dimensions: ["org_view_states.code"],
    order: {
      "org_view_states.code": "asc"
    }
  },
  organization: {
    dimensions: [
      "org_view_organizations.organizationId",
      "org_view_organizations.organizationName"
    ],
    filters: [
      {
        member: "org_view_organizations.states",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_organizations.organizationName": "asc"
    }
  },
  district: {
    dimensions: [
      "org_view_districts.districtId",
      "org_view_districts.districtName",
      "org_view_districts.state"
    ],
    filters: [
      {
        member: "org_view_districts.state",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_districts.organizationId",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_districts.districtName": "asc"
    }
  },
  school: {
    dimensions: [
      "org_view_schools.schoolId",
      "org_view_schools.name",
      "org_view_schools.state",
      "org_view_schools.districtId"
    ],
    filters: [
      {
        member: "org_view_schools.state",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_schools.organizationId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_schools.districtId",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_schools.name": "asc"
    }
  },
  class: {
    dimensions: [
      "org_view_classes.classId",
      "org_view_classes.name",
      "org_view_classes.state",
      "org_view_classes.districtId",
      "org_view_classes.schoolId",
      "org_view_classes.teacherId"
    ],
    filters: [
      {
        member: "org_view_classes.state",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_classes.organizationId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_classes.districtId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_classes.schoolId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_classes.teacherId",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_classes.name": "asc"
    }
  },
  teacher: {
    dimensions: [
      "org_view_teachers.teacherId",
      "org_view_teachers.fullName",
      "org_view_teachers.state",
      "org_view_teachers.districtId",
      "org_view_teachers.schoolId"
    ],
    filters: [
      {
        member: "org_view_teachers.state",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_teachers.organizationId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_teachers.districtId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_teachers.schoolId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_teachers.classId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_teachers.role",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_teachers.fullName": "asc"
    }
  },
  student: {
    dimensions: [
      "org_view_students.studentId",
      "org_view_students.studentFullname",
      "org_view_students.state",
      "org_view_students.districtId",
      "org_view_students.schoolId"
    ],
    measures: ["org_view_students.teacherIds", "org_view_students.classIds"],
    filters: [
      {
        member: "org_view_students.state",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.organizationId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.districtId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.schoolId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.teacherId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.classId",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.grade",
        operator: "equals",
        values: []
      },
      {
        member: "org_view_students.role",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "org_view_students.studentFullname": "asc"
    }
  },
  userStatus: {
    dimensions: ["org_view_students.isActive"],
    filters: [
      {
        member: "org_view_students.isActive",
        operator: "equals",
        values: []
      }
    ]
  },
  content: {
    dimensions: [
      "content_view_subtests.id",
      "content_view_subtests.name",
      "content_view_subtests.program",
      "content_view_subtests.subject"
    ],
    filters: [
      // {
      //   member: "content_view_list.type",
      //   operator: "equals",
      //   values: []
      // },
      {
        member: "content_view_subtests.program",
        operator: "equals",
        values: []
      },
      {
        member: "content_view_subtests.subject",
        operator: "equals",
        values: []
      }
    ],
    order: {
      "content_view_subtests.name": "asc"
    },
    limit: 300
  },
  questionsContent: {
    dimensions: ["content_view_subtests.id"]
  },
  contentType: {
    dimensions: ["content.type"]
  },
  usageContentType: {
    dimensions: ["content.type"]
  },
  activity: {
    dimensions: ["eventTypes.code"]
  },
  userRole: {
    dimensions: ["events.userRole"]
  },
  adminRole: {
    dimensions: ["org_view_admins.role"]
  },
  test: {
    dimensions: [PROGRAM_FILTER_REQUEST_PARAM, SUBJECT_FILTER_REQUEST_PARAM]
  },
  level: {
    dimensions: ["level"]
  },
  grade: {
    dimensions: ["org_view_students.grade"]
  },
  skill: {
    dimensions: ["content.skill", "content.skillName"]
  },
  licenseStatus: {
    dimensions: ["org_view_students.hasActiveLicense"],
    filters: [
      {
        member: "org_view_students.hasActiveLicense",
        operator: "equals",
        values: []
      }
    ]
  }
}

export const ADDITIONAL_FILTER_REQUEST_FIELD_BY_ENTITY_ROUTE: Record<
  string,
  Record<string, Record<string, string>>
> = {
  organization: {
    schools_id: {
      class: "org_view_classes.schoolId",
      teacher: "org_view_teachers.schoolId"
    },
    teachers_id: {
      class: "org_view_classes.teacherId",
      student: "org_view_students.teacherId"
    },
    classes_id: {
      student: "org_view_students.classId"
    }
  }
}

export const PARENT_FILTERS: Record<string, string[]> = {
  state: [],
  district: ["state", "organization"],
  school: ["state", "organization", "district"],
  class: ["state", "organization", "district", "school", "teacher"],
  teacher: ["state", "organization", "district", "school", "class", "userRole"],
  student: [
    "state",
    "organization",
    "district",
    "school",
    "teacher",
    "class",
    "grade",
    "userRole"
  ],
  content: ["test", "contentType"]
}

export const PARENT_FILTERS_MAP = {
  content: {
    test: ["content_view_list.program", "content_view_list.subject"],
    contentType: "content_view_list.type"
  },
  teacher: {
    userRole: "org_view_teachers.role"
  },
  student: {
    userRole: "org_view_students.role"
  }
}

export const PARENT_TEST_FILTER_MEMBER_MAP = {
  content: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_list.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_list.subject"
  }
}

export const NO_GRADE_LABEL = "No Grade"
export const GRADUATED_LABEL = "Graduated"

export const FILTER_OPTIONS = {
  state: Object.keys(States)
    .sort()
    .map(state => ({
      label: state,
      value: state
    })),
  grade: [
    { label: "8", value: "8" },
    { label: "9", value: "9" },
    { label: "10", value: "10" },
    { label: "11", value: "11" },
    { label: "12", value: "12" },
    { label: NO_GRADE_LABEL, value: "unknown" },
    { label: GRADUATED_LABEL, value: "graduated" }
  ],
  contentType: [
    {
      label: getWorkkeysReplaceTerm(ContentType.practiceTest, true),
      value: ContentType.practiceTest
    },
    {
      label: ContentTypeLabels[ContentType.masteryLesson],
      value: ContentType.masteryLesson
    },
    {
      label: ContentTypeLabels[ContentType.digitalResource],
      value: ContentType.digitalResource
    }
    // {
    //   label: ContentTypeLabels[ContentType.masteryCurriculum],
    //   value: ContentType.masteryCurriculum
    // },
    // {
    //   label: ContentTypeLabels[ContentType.bellRinger],
    //   value: ContentType.bellRinger
    // },
    // {
    //   label: ContentTypeLabels[ContentType.skillReview],
    //   value: ContentType.skillReview
    // }
  ],
  usageContentType: [
    {
      label: getWorkkeysReplaceTerm("practice_test", true),
      value: "TEST",
      level: 0
    },
    {
      label: getWorkkeysReplaceTerm("diagnostic_test"),
      value: "DIAGNOSTIC_TEST",
      level: 0
    },
    {
      label: "Online Lesson",
      value: "ML",
      level: 0,
      optionDetails: { children: ["ML_LESSON", "ML_PRACTICE_SET"] }
    },
    {
      label: "Mini-Lesson",
      value: "ML_LESSON",
      level: 1,
      optionDetails: { parent: "ML" }
    },
    {
      label: "Practice Set",
      value: "ML_PRACTICE_SET",
      level: 1,
      optionDetails: { parent: "ML" }
    }
    // {
    //   label: "Mastery Curriculum",
    //   value: "MC",
    //   level: 0,
    //   optionDetails: { children: ["MC_ENTRY_TICKET", "MC_EXIT_TICKET"] }
    // },
    // {
    //   label: "Entry Ticket",
    //   value: "MC_ENTRY_TICKET",
    //   level: 1,
    //   optionDetails: { parent: "MC" }
    // },
    // {
    //   label: "Exit Ticket",
    //   value: "MC_EXIT_TICKET",
    //   level: 1,
    //   optionDetails: { parent: "MC" }
    // },
    // {
    //   label: "Bell Ringer",
    //   value: "BELL_RINGER",
    //   level: 0
    // }
    // {
    //   label: "Skill Review",
    //   value: "SH",
    //   level: 0,
    //   optionDetails: { children: ["SH_VIDEO", "SH_PRACTICE_SET"] }
    // },
    // {
    //   label: "Video Review",
    //   value: "SH_VIDEO",
    //   level: 1,
    //   optionDetails: { parent: "SH" }
    // },
    // {
    //   label: "Practice Set",
    //   value: "SH_PRACTICE_SET",
    //   level: 1,
    //   optionDetails: { parent: "SH" }
    // }
  ],
  activity: [
    {
      label: ActivityEventsLabels[EventAction.START],
      value: EventAction.START
    },
    {
      label: ActivityEventsLabels[EventAction.PAUSE],
      value: EventAction.PAUSE
    },
    {
      label: ActivityEventsLabels[EventAction.RESUME],
      value: EventAction.RESUME
    },
    {
      label: ActivityEventsLabels[EventAction.FINISH],
      value: EventAction.FINISH
    },
    {
      label: ActivityEventsLabels[EventAction.REVIEW],
      value: EventAction.REVIEW
    },
    {
      label: ActivityEventsLabels[EventAction.PREVIEW],
      value: EventAction.PREVIEW
    },
    // {
    //   label: ActivityEventsLabels[EventAction.ANSWER],
    //   value: EventAction.ANSWER
    // },
    // {
    //   label: ActivityEventsLabels[EventAction.LESSON_COMPLETE],
    //   value: EventAction.LESSON_COMPLETE
    // },
    // {
    //   label: ActivityEventsLabels[EventAction.LESSON_RESUME],
    //   value: EventAction.LESSON_RESUME
    // },
    // {
    //   label: ActivityEventsLabels[EventAction.LESSON_START],
    //   value: EventAction.LESSON_START
    // },
    // {
    //   label: ActivityEventsLabels[EventAction.VIEW],
    //   value: EventAction.VIEW
    // },
    // {
    //   label: ActivityEventsLabels[EventAction.QUESTION_ANSWERED],
    //   value: EventAction.QUESTION_ANSWERED
    // },
    {
      label: ActivityEventsLabels[EventAction.LOGIN],
      value: EventAction.LOGIN
    },
    {
      label: ActivityEventsLabels[EventAction.LOGOUT],
      value: EventAction.LOGOUT
    }
  ],
  userRole: [
    { label: getWorkkeysReplaceTerm("student", true), value: UserRole.student },
    { label: getWorkkeysReplaceTerm("teacher", true), value: UserRole.teacher }
  ],
  adminRole: [
    { label: "Organization Admin", value: UserRole.actMultiDistrictAdmin },
    { label: "District Admin", value: UserRole.districtAdmin },
    {
      label: getWorkkeysReplaceTerm("school admin", true),
      value: UserRole.schoolAdmin
    }
  ],
  userStatus: [
    { label: "No Activity", value: "false" },
    { label: "Active", value: "true" }
  ],
  licenseStatus: [
    { label: LicenseStatus.LICENSED, value: LicenseStatus.LICENSED },
    { label: LicenseStatus.UNLICENSED, value: LicenseStatus.UNLICENSED }
  ]
}

export const GRADE_FILTER_OPTIONS = [
  { label: GradeFilterOption.GRADE_9, value: GradeFilterOption.GRADE_9 },
  { label: GradeFilterOption.GRADE_10, value: GradeFilterOption.GRADE_10 },
  { label: GradeFilterOption.GRADE_11, value: GradeFilterOption.GRADE_11 },
  { label: GradeFilterOption.GRADE_12, value: GradeFilterOption.GRADE_12 }
]

export const USAGE_TO_CONTENT_TYPES = {
  TEST: ContentType.practiceTest,
  DIAGNOSTIC_TEST: ContentType.practiceTest,
  ML_LESSON: ContentType.masteryLesson,
  ML_PRACTICE_SET: ContentType.masteryLesson,
  MC_ENTRY_TICKET: ContentType.masteryCurriculum,
  MC_EXIT_TICKET: ContentType.masteryCurriculum,
  BELL_RINGER: ContentType.bellRinger,
  SH_VIDEO: ContentType.skillReview,
  SH_PRACTICE_SET: ContentType.skillReview
}

export const CONTENT_TYPES_TO_USAGE: Record<ContentType, string[]> = {
  [ContentType.practiceTest]: ["TEST", "DIAGNOSTIC_TEST"],
  [ContentType.masteryLesson]: ["ML_LESSON", "ML_PRACTICE_SET"],
  [ContentType.masteryCurriculum]: ["MC_ENTRY_TICKET", "MC_EXIT_TICKET"],
  [ContentType.bellRinger]: ["BELL_RINGER"],
  [ContentType.skillReview]: ["SH_VIDEO", "SH_PRACTICE_SET"],
  [ContentType.digitalResource]: []
}

export const CONTENT_TYPE_TO_URL_MAPPING = {
  [ContentType.bellRinger]: "element",
  [ContentType.skillReview]: "study-hall",
  [ContentType.practiceTest]: getWorkkeysReplaceUrlContentType("practice-test"),
  [ContentType.masteryLesson]: "mastery-lesson",
  [ContentType.masteryCurriculum]: "mastery-curriculum",
  [ContentType.digitalResource]: "digital-resource"
}

export const ENTITY_TO_FILTER_MAPPING: Record<string, string[]> = {
  districts: ["district"],
  schools: ["school"],
  teachers: ["teacher"],
  classes: ["class"],
  students: ["student"]
}

export const FILTERS_WITHOUT_CACHE_TIME_FIELDS = {
  district: "org_view_districts.createdAt",
  school: "org_view_schools.createdAt",
  teacher: "org_view_teachers.createdAt",
  class: "org_view_classes.createdAt",
  student: "org_view_students.createdAt"
}

export const EXCLUDE_EXPORT_FILTERS_BY_ROLE = {
  insights: {
    "activity-log": {
      [Roles.Teacher]: ["teacher"]
    }
  }
}

export const MANDATORY_IMPORT_FILTERS = ["school"]

// Configuration for filter options by route
export const FILTERS_CONFIG_BY_ROUTE: Record<string, FilterRouteConfig> = {
  "/reporting/insights/performance/questions": {
    filters: FILTERS_BY_ROUTE["/reporting/insights/performance/questions"],
    filterOptions: {
      test: {
        setQuestionsAvailablePrograms: true // Flag for setting available programs of questions that under the feature flag
      }
    }
  },
  "/reporting/teaching-tools/test-administration": {
    filters: FILTERS_BY_ROUTE["/reporting/teaching-tools/test-administration"],
    filterOptions: {
      test: {
        singleLevel: true // Only show programs, not courses
      }
    }
  },
  "/reporting/teaching-tools/test-administration/:entityId": {
    filters:
      FILTERS_BY_ROUTE[
        "/reporting/teaching-tools/test-administration/:entityId"
      ],
    filterOptions: {
      test: {
        singleLevel: true // Only show programs, not courses
      }
    }
  },
  "/reporting/teaching-tools/test-administration/:entityId/edit": {
    filters:
      FILTERS_BY_ROUTE[
        "/reporting/teaching-tools/test-administration/:entityId/edit"
      ],
    filterOptions: {
      test: {
        singleLevel: true // Only show programs, not courses
      }
    }
  }
}

export const HIDDEN_FILTERS_FROM_FILTER_BAR = ["test"]

export const HIDDEN_FILTERS_FROM_FILTER_BAR_BY_ROUTE: Record<string, string[]> =
  {
    "/reporting/teaching-tools/books/mastery-curriculum": ["test"],
    "/reporting/insights/performance/questions": ["questionsContent"],
    "/reporting/insights/performance/scores": ["contentType"]
  }

export const HIDDEN_FILTERS_FROM_FILTER_BAR_BY_ROLE: Record<
  string,
  Record<string, string[]>
> = {
  "/reporting/organization/license-pools": {
    [Roles.ACT_Multi_District_Admin]: ["state", "organization", "district"],
    [Roles.District_Admin]: ["state", "organization", "district"],
    [Roles.School_Admin]: ["state", "organization", "district"]
  }
}

export const FULL_OVERRIDE_FILTER_BY_ROUTE_ROLE = {
  "/reporting/content/content-library": {
    featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
    roles: {
      [Roles.Super_Administrator]: {
        shown: ["level", "test", "contentType", "skill", "standard"],
        hidden: ["district", "school", "teacher", "class", "student"]
      },
      [Roles.ACT_Admin]: {
        shown: ["level", "test", "contentType", "skill", "standard"],
        hidden: ["district", "school", "teacher", "class", "student"]
      },
      [Roles.District_Admin]: {
        shown: ["level", "test", "contentType", "skill", "standard"],
        hidden: ["school", "teacher", "class", "student"]
      },
      [Roles.School_Admin]: {
        shown: ["level", "test", "contentType", "skill", "standard"],
        hidden: ["teacher", "class", "student"]
      },
      [Roles.Teacher]: {
        shown: ["level", "test", "contentType", "skill", "standard"],
        hidden: ["class", "student"]
      }
    }
  }
}
